/*
 * Mock market feed for the USDX Trade Terminal design pass.
 *
 * Everything here is deterministic (seeded PRNG) so the layout looks identical
 * on every reload. Swap these generators for the real OHLCV / L2 / trade feeds
 * when the terminal is wired up.
 */

export type Candle = { o: number; h: number; l: number; c: number; v: number };
export type BookRow = { p: number; s: number };
export type TradeRow = { p: number; s: number; up: boolean; t: number };
export type Book = { asks: BookRow[]; bids: BookRow[] };

export type MarketState = {
  candles: Candle[];
  price: number;
  open24: number;
  /** Tick counter — drives when the live candle rolls over. */
  tick: number;
  book: Book;
  trades: TradeRow[];
  clock: string;
};

export const CANDLE_COUNT = 84;
export const BOOK_ROWS = 11;
export const TRADE_ROWS = 18;
export const TICK_MS = 1400;
export const PRICE_FLOOR = 60_800;
/** Ticks before the live candle closes and a fresh one opens. */
export const TICKS_PER_CANDLE = 20;
/** How far the mock price may drift from the 24h open, either way. */
export const PRICE_BAND = 0.035;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export const TIMEFRAMES = ["1m", "5m", "15m", "1h", "4h", "1d", "1w"] as const;
export const ORDER_TYPES = ["Market", "Limit", "Pro"] as const;
export const SIZE_PERCENTS = [0, 25, 50, 75, 100] as const;

export type Timeframe = (typeof TIMEFRAMES)[number];
export type OrderType = (typeof ORDER_TYPES)[number];

/** mulberry32 — small, fast, and stable across engines. */
export function createRng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function utcClock(ms: number) {
  return new Date(ms).toISOString().slice(11, 19);
}

export function num(value: number, digits: number) {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function usd(value: number, digits = 2) {
  return `$${num(value, digits)}`;
}

function makeCandles(rnd: () => number): Candle[] {
  const candles: Candle[] = [];
  let price = 61_200;

  for (let i = 0; i < CANDLE_COUNT; i++) {
    const o = price;
    price = o + (rnd() - 0.44) * 190;
    candles.push({
      o,
      h: Math.max(o, price) + rnd() * 70,
      l: Math.min(o, price) - rnd() * 70,
      c: price,
      v: 0.2 + rnd() * 0.8,
    });
  }

  return candles;
}

function makeBook(rnd: () => number, price: number): Book {
  const asks: BookRow[] = [];
  const bids: BookRow[] = [];

  for (let i = 0; i < BOOK_ROWS; i++) {
    asks.push({ p: price + (BOOK_ROWS - i), s: 0.05 + rnd() * 9 });
    bids.push({ p: price - (i + 1), s: 0.05 + rnd() * 9 });
  }

  return { asks, bids };
}

function makeTrades(rnd: () => number, price: number, now: number): TradeRow[] {
  const trades: TradeRow[] = [];

  for (let i = 0; i < TRADE_ROWS; i++) {
    trades.push({
      p: price + (rnd() - 0.5) * 12,
      s: 0.02 + rnd() * 3,
      up: rnd() > 0.45,
      t: now - i * 4200,
    });
  }

  return trades;
}

export function createInitialMarket(rnd: () => number, now: number): MarketState {
  const candles = makeCandles(rnd);
  const price = candles[candles.length - 1].c;

  return {
    candles,
    price,
    open24: price * 0.9893,
    tick: 0,
    book: makeBook(rnd, price),
    trades: makeTrades(rnd, price, now),
    clock: utcClock(now),
  };
}

/** One 1400ms tick: nudge the live candle, jitter the book, prepend a trade. */
export function advanceMarket(prev: MarketState, rnd: () => number, now: number): MarketState {
  const candles = prev.candles.slice();
  const last = { ...candles[candles.length - 1] };

  /*
   * The walk is clamped into a band around the 24h open. Without this the live
   * candle's high/low only ever expand, so after a few minutes that one candle
   * dominates the y-scale and flattens the rest of the series.
   */
  const price = clamp(
    last.c + (rnd() - 0.5) * 70,
    Math.max(PRICE_FLOOR, prev.open24 * (1 - PRICE_BAND)),
    prev.open24 * (1 + PRICE_BAND)
  );

  last.c = price;
  last.h = Math.max(last.h, price);
  last.l = Math.min(last.l, price);
  candles[candles.length - 1] = last;

  /*
   * Close the live candle on a fixed cadence and open a fresh one, dropping the
   * oldest so the series length stays put — same reason as the clamp above.
   */
  const tick = (prev.tick ?? 0) + 1;
  if (tick % TICKS_PER_CANDLE === 0) {
    candles.push({ o: price, h: price, l: price, c: price, v: 0.2 + rnd() * 0.8 });
    candles.shift();
  }

  const jitter = (row: BookRow): BookRow => ({
    p: row.p,
    s: Math.max(0.03, row.s + (rnd() - 0.5) * 1.6),
  });

  return {
    candles,
    price,
    open24: prev.open24,
    tick,
    book: { asks: prev.book.asks.map(jitter), bids: prev.book.bids.map(jitter) },
    trades: [{ p: price, s: 0.02 + rnd() * 3, up: price >= prev.price, t: now }].concat(
      prev.trades.slice(0, TRADE_ROWS - 1)
    ),
    clock: utcClock(now),
  };
}

export const TICKER_ITEMS = [
  { sym: "BTC", px: 62_790.0, chg: 1.07, up: false },
  { sym: "ETH", px: 3184.22, chg: 2.14, up: true },
  { sym: "SOL", px: 147.36, chg: 4.02, up: true },
  { sym: "ARB", px: 0.9124, chg: 1.88, up: false },
  { sym: "USDX", px: 1.0002, chg: 0.01, up: true },
  { sym: "LINK", px: 17.42, chg: 0.64, up: true },
];

export const BALANCES = [
  {
    asset: "USDC (USDX)",
    total: "12,480.00",
    available: "10,180.00",
    inOrders: "2,300.00",
    value: "$12,480.00",
  },
  { asset: "BTC (Spot)", total: "0.1340", available: "0.1340", inOrders: "0.0000", value: "$8,414.86" },
  { asset: "USDX", total: "5,000.00", available: "5,000.00", inOrders: "0.00", value: "$5,001.00" },
];

export const BOTTOM_TABS = [
  "Balances",
  "Positions",
  "Open Orders",
  "TWAP",
  "Trade History",
  "Funding History",
  "Order History",
];

export const TIME_AXIS_WIDE = [
  "16:30",
  "18:00",
  "19:30",
  "21:00",
  "22:30",
  "00:00",
  "01:30",
  "03:00",
  "05:00",
];
export const TIME_AXIS_NARROW = ["18:00", "21:00", "00:00", "03:00"];

/** Account / position figures shown in the design. */
export const ACCOUNT = {
  availableToTrade: 12_480,
  positionSize: 0.184,
  entryPrice: 61_914.2,
  spotEquity: 8420.16,
  usdxEquity: 12_480,
  unrealizedPnl: 412.88,
  marginUsage: 18.4,
};
